import { getActiveAccessToken, isImpersonatingToken } from '@/features/impersonation-session';
import { isDesktop } from '@/lib/platform';
import { newRelicService } from '@/services/newrelic';
import { supabase } from '@/services/supabase';

const handleAuthRedirect = () => {
  // Desktop uses memory history; the SIGNED_OUT auth-state listener in
  // __root.tsx handles redirect via router.invalidate() instead.
  if (!isDesktop()) {
    window.location.href = '/login';
  }
};

const buildHeaders = (input: Request, token: string | null): Headers => {
  const headers = new Headers(input.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
};

const normalizePathSegment = (segment: string) => {
  const decoded = (() => {
    try {
      return decodeURIComponent(segment);
    } catch {
      return segment;
    }
  })();

  if (/^\+?\d{7,}$/.test(decoded)) {
    return ':phone';
  }

  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(decoded)) {
    return ':uuid';
  }

  if (/^[a-z]{2,6}_[a-zA-Z0-9_-]{8,}$/.test(decoded)) {
    return `:${decoded.split('_')[0]}`;
  }

  if (/^[a-zA-Z0-9_-]{16,}$/.test(decoded)) {
    return ':id';
  }

  return decoded;
};

const getSafeRequestPath = (url: string) => {
  try {
    const { pathname } = new URL(url);
    return pathname
      .split('/')
      .map((segment) => (segment ? normalizePathSegment(segment) : segment))
      .join('/');
  } catch {
    return 'unknown';
  }
};

const trackAuth401 = (
  request: Request,
  attributes: {
    outcome:
      | 'impersonation_skipped'
      | 'missing_token'
      | 'refresh_failed'
      | 'refresh_missing_session'
      | 'refresh_threw'
      | 'retry_failed'
      | 'retry_succeeded'
      | 'retry_threw';
    hadToken: boolean;
    isImpersonating: boolean;
    refreshError?: boolean;
    retryStatus?: number;
  },
) => {
  newRelicService.addPageAction('api:auth:401', {
    domain: 'auth',
    method: request.method,
    path: getSafeRequestPath(request.url),
    status: 401,
    ...attributes,
  });
};

export const fetchWithAuth = async (input: Request): Promise<Response> => {
  const token = await getActiveAccessToken();
  if (!token) {
    handleAuthRedirect();
  }

  // `new Request(input, { headers })` preserves method/body/signal explicitly;
  // `fetch(input, { headers })` is spec-ambiguous about body across browsers.
  const request = new Request(input, { headers: buildHeaders(input, token) });
  const isImpersonating = token ? isImpersonatingToken(token) : false;
  const retryRequest = token && !isImpersonating ? request.clone() : null;

  const response = await fetch(request);
  if (response.status !== 401) {
    return response;
  }

  if (!retryRequest) {
    trackAuth401(request, {
      outcome: token ? 'impersonation_skipped' : 'missing_token',
      hadToken: !!token,
      isImpersonating,
    });
    return response;
  }

  // Single retry: refresh the Supabase token and reissue with the new one.
  // Supabase's SDK single-flights concurrent refreshSession() calls internally
  // (refreshingDeferred in @supabase/auth-js GoTrueClient), so we don't need
  // our own in-flight guard.
  const { data, error } = await supabase.auth.refreshSession().catch((error) => {
    trackAuth401(request, {
      outcome: 'refresh_threw',
      hadToken: true,
      isImpersonating: false,
      refreshError: true,
    });
    throw error;
  });
  const newToken = data.session?.access_token;
  if (!newToken) {
    handleAuthRedirect();
    trackAuth401(request, {
      outcome: error ? 'refresh_failed' : 'refresh_missing_session',
      hadToken: true,
      isImpersonating: false,
      refreshError: !!error,
    });
    return response;
  }

  const retryResponse = await fetch(
    new Request(retryRequest, { headers: buildHeaders(retryRequest, newToken) }),
  ).catch((error) => {
    trackAuth401(request, {
      outcome: 'retry_threw',
      hadToken: true,
      isImpersonating: false,
    });
    throw error;
  });

  trackAuth401(request, {
    outcome: retryResponse.ok ? 'retry_succeeded' : 'retry_failed',
    hadToken: true,
    isImpersonating: false,
    retryStatus: retryResponse.status,
  });

  return retryResponse;
};
